// Admin → Ambassadors: the Ambassador Calculator's math, and nothing else (no
// React, no network). An ambassador posts Reels and Stories for Request and,
// instead of a sponsorship fee, earns a share of Request's retained PLATFORM
// revenue (our fees, not request volume or talent earnings). The share is
// sized so the Base Case forecast pays roughly what that promotion is worth:
//
//   annual market value      = Σ posts per year × avg views / 1,000 × CPM
//   annual partnership value = annual market value × strategic multiplier
//                              (from the Strategic Influence Score — score.ts —
//                              unless one is typed by hand)
//   partnership market value = annual partnership value × years
//   target compensation      = partnership market value × risk premium
//   base rate                = target / Σ (forecast[y] × taper weight[y])
//   a period's share         = base rate × its weight, or a manual %
//   earnings[scenario][y]    = forecast[y] × scenario % × share[y]
//
// The scenarios never move the share: they show what the same terms pay if
// revenue lands at 50% / 100% / 175% of the forecast. A blank input is null and
// everything it feeds reads null ("—" on screen), so a half-filled form never
// shows a confident wrong number. Full precision throughout; only the
// formatters round.
import type {
  FactorDef,
  OfferInputs,
  OfferResults,
  PeriodResult,
  SavedOffer,
  SharePeriod,
  YearRow,
} from "../../types/ambassadors";
import { yearsLabel } from "./format";
import { DEFAULT_FACTORS, multiplierFor, neutralFactors, scoreFactorsFrom, strategicScore } from "./score";

type N = number | null;

export const MIN_YEARS = 1;
export const MAX_YEARS = 10;
const DEFAULT_YEARS = 6;
// Request's retained platform revenue, base case, Year 1 first.
export const DEFAULT_REVENUE = [250_000, 1_000_000, 3_000_000, 5_000_000, 7_200_000, 10_000_000];
// Earlier years pay more: 3× the base rate, then 1.5×, then 1×.
export const DEFAULT_WEIGHTS = [3, 1.5, 1];

// A new offer: the founder's DJ Lucky example, rated neutral (5/10) on the
// shared score factors `defs`.
export function defaultInputs(defs: FactorDef[] = DEFAULT_FACTORS): OfferInputs {
  return {
    djName: "DJ Lucky",
    instagramHandle: "",
    photo: "",
    notes: "",
    followers: 25_000,
    reelViews: 12_000,
    storyViews: 8_000,
    reelsPerQuarter: 2,
    storiesPerQuarter: 2,
    reelCpm: 35,
    storyCpm: 20,
    score: { factors: neutralFactors(defs) },
    strategicMultiplier: null,
    riskPremium: 1.5,
    years: DEFAULT_YEARS,
    revenue: [...DEFAULT_REVENUE],
    scenarioPct: { conservative: 50, base: 100, upside: 175 },
    periods: defaultPeriods(DEFAULT_YEARS),
  };
}

// A usable amount, or null for blank / invalid / negative.
const val = (v: unknown): N => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null);
function mul(...xs: N[]): N {
  let r = 1;
  for (const x of xs) {
    if (x === null) return null;
    r *= x;
  }
  return r;
}
function add(...xs: N[]): N {
  let r = 0;
  for (const x of xs) {
    if (x === null) return null;
    r += x;
  }
  return r;
}
const div = (a: N, b: number): N => (a === null ? null : a / b);

export function clampYears(y: unknown): number {
  return typeof y === "number" && Number.isFinite(y)
    ? Math.min(MAX_YEARS, Math.max(MIN_YEARS, Math.round(y)))
    : DEFAULT_YEARS;
}

// The default schedule for `years`: the 3× / 1.5× / 1× tiers laid end to end.
// Up to 3 years, one year per tier. From 4, Years 1–2 come first and the rest
// splits in two, the earlier half taking the odd year (5 → 1–2, 3–4, 5 ·
// 6 → 1–2, 3–4, 5–6 · 8 → 1–2, 3–5, 6–8). `keep` carries an existing
// schedule's weights and manual shares over, tier by tier.
export function defaultPeriods(years: number, keep: SharePeriod[] = []): SharePeriod[] {
  const y = clampYears(years);
  const lengths = y <= 3 ? Array<number>(y).fill(1) : [2, Math.ceil((y - 2) / 2), Math.floor((y - 2) / 2)];
  let start = 1;
  return lengths.map((len, i) => {
    const p: SharePeriod = {
      start,
      end: start + len - 1,
      weight: keep[i] ? (val(keep[i].weight) ?? DEFAULT_WEIGHTS[i]) : DEFAULT_WEIGHTS[i],
      ratePct: keep[i] ? val(keep[i].ratePct) : null,
    };
    start += len;
    return p;
  });
}

// Lays periods end to end from Year 1: each starts the year after the one
// before it ends and keeps at least one year; the last always ends on `years`.
// Gaps and overlaps cannot survive this.
export function normalizePeriods(periods: SharePeriod[], years: number): SharePeriod[] {
  const y = clampYears(years);
  const list = (Array.isArray(periods) ? periods : []).slice(0, y);
  if (list.length === 0) return defaultPeriods(y);
  let start = 1;
  return list.map((p, i) => {
    const roomAfter = list.length - 1 - i;
    const wanted = typeof p.end === "number" && Number.isFinite(p.end) ? Math.round(p.end) : start;
    const end = roomAfter === 0 ? y : Math.min(Math.max(wanted, start), y - roomAfter);
    const out: SharePeriod = { start, end, weight: val(p.weight), ratePct: val(p.ratePct) };
    start = end + 1;
    return out;
  });
}

// The years period `index` may end on: its own start at the earliest, and
// early enough that every later period keeps a year.
export function endChoices(periods: SharePeriod[], index: number, years: number): number[] {
  const p = periods[index];
  if (!p) return [];
  const last = years - (periods.length - 1 - index);
  const out: number[] = [];
  for (let y = p.start; y <= last; y++) out.push(y);
  return out;
}

export function setPeriodEnd(periods: SharePeriod[], index: number, end: number, years: number): SharePeriod[] {
  return normalizePeriods(periods.map((p, k) => (k === index ? { ...p, end } : p)), years);
}

// Splits the last period that spans two or more years: its final year becomes a
// new period with the same weight and share, ready to edit.
export function addPeriod(periods: SharePeriod[], years: number): SharePeriod[] {
  const ps = normalizePeriods(periods, years);
  for (let k = ps.length - 1; k >= 0; k--) {
    const p = ps[k];
    if (p.end > p.start) {
      return normalizePeriods([...ps.slice(0, k), { ...p, end: p.end - 1 }, { ...p, start: p.end }, ...ps.slice(k + 1)], years);
    }
  }
  return ps;
}

// Removes a period; its years go to the period before it (the first period's
// go to the one after).
export function removePeriod(periods: SharePeriod[], index: number, years: number): SharePeriod[] {
  const ps = normalizePeriods(periods, years);
  if (ps.length <= 1 || !ps[index]) return ps;
  const rest = ps.filter((_, k) => k !== index);
  if (index > 0) rest[index - 1] = { ...rest[index - 1], end: ps[index].end };
  return normalizePeriods(rest, years);
}

export const clearManualRates = (periods: SharePeriod[]): SharePeriod[] => periods.map((p) => ({ ...p, ratePct: null }));

// A new duration gets a fresh default schedule (keeping the tiers' weights and
// manual shares). Revenue already typed for a year is kept; a year with no
// forecast stays blank until someone enters one.
export function withYears(inputs: OfferInputs, years: number): OfferInputs {
  const y = clampYears(years);
  return { ...inputs, years: y, periods: defaultPeriods(y, normalizePeriods(inputs.periods, inputs.years)) };
}

export function calculate(i: OfferInputs): OfferResults {
  const issues: string[] = [];
  const need = (v: unknown, label: string): N => {
    const n = val(v);
    if (n === null) issues.push(label);
    return n;
  };

  if (!i.djName.trim()) issues.push("DJ name");
  const reelViews = need(i.reelViews, "Average Reel views");
  const storyViews = need(i.storyViews, "Average Story views");
  const reelsPerQuarter = need(i.reelsPerQuarter, "Reels per quarter");
  const storiesPerQuarter = need(i.storiesPerQuarter, "Stories per quarter");
  const reelCpm = need(i.reelCpm, "Reel CPM");
  const storyCpm = need(i.storyCpm, "Story CPM");
  // The multiplier typed by hand wins; otherwise the score's.
  const manualStrategic = val(i.strategicMultiplier);
  const score = strategicScore(i.score.factors);
  if (manualStrategic === null && score === null) issues.push("Strategic Influence Score");
  const strategic = manualStrategic ?? (score === null ? null : multiplierFor(score));
  const risk = need(i.riskPremium, "Risk premium");
  const years = clampYears(i.years);
  const revenue = Array.from({ length: years }, (_, k) => need(i.revenue[k], `Year ${k + 1} revenue`));
  const scenario = {
    conservative: div(need(i.scenarioPct.conservative, "Conservative %"), 100),
    base: div(need(i.scenarioPct.base, "Base %"), 100),
    upside: div(need(i.scenarioPct.upside, "Upside %"), 100),
  };

  const reelsPerYear = mul(reelsPerQuarter, 4);
  const storiesPerYear = mul(storiesPerQuarter, 4);
  const annualReelValue = mul(reelsPerYear, div(reelViews, 1000), reelCpm);
  const annualStoryValue = mul(storiesPerYear, div(storyViews, 1000), storyCpm);
  const annualMarketValue = add(annualReelValue, annualStoryValue);
  const annualPartnershipValue = mul(annualMarketValue, strategic);
  const partnershipMarketValue = mul(annualPartnershipValue, years);
  const targetCompensation = mul(partnershipMarketValue, risk);

  const periods = normalizePeriods(i.periods, years);
  const periodIndex = (year: number) => periods.findIndex((p) => year >= p.start && year <= p.end);
  // A weight only matters while some period still uses the calculated share.
  const needsCalculated = periods.some((p) => p.ratePct === null);
  const weights = periods.map((p) =>
    needsCalculated ? need(p.weight, `Weight for ${yearsLabel(p.start, p.end)}`) : p.weight
  );

  // Calibrated on the forecast as entered, so no scenario % moves the share.
  const weighted = add(...revenue.map((r, k) => mul(r, weights[periodIndex(k + 1)])));
  let baseRatePct: N = null;
  if (targetCompensation !== null && weighted !== null) {
    if (weighted > 0) baseRatePct = (targetCompensation / weighted) * 100;
    else if (needsCalculated) {
      issues.push(add(...revenue) === 0 ? "a revenue forecast above $0" : "a taper weight above 0");
    }
  }

  const periodResults: PeriodResult[] = periods.map((p, k) => {
    const calculatedPct = mul(baseRatePct, weights[k]);
    return {
      start: p.start,
      end: p.end,
      weight: p.weight,
      calculatedPct,
      ratePct: p.ratePct ?? calculatedPct,
      manual: p.ratePct !== null,
    };
  });

  const rows: YearRow[] = revenue.map((r, k) => {
    const ratePct = periodResults[periodIndex(k + 1)].ratePct;
    const share = div(ratePct, 100);
    return {
      year: k + 1,
      revenue: r,
      baseRevenue: mul(r, scenario.base),
      ratePct,
      conservative: mul(r, scenario.conservative, share),
      base: mul(r, scenario.base, share),
      upside: mul(r, scenario.upside, share),
    };
  });

  return {
    issues,
    reelsPerYear,
    storiesPerYear,
    totalReels: mul(reelsPerYear, years),
    totalStories: mul(storiesPerYear, years),
    annualReelValue,
    annualStoryValue,
    annualMarketValue,
    strategicScore: score,
    strategicMultiplier: strategic,
    strategicManual: manualStrategic !== null,
    annualPartnershipValue,
    partnershipMarketValue,
    targetCompensation,
    baseRatePct,
    periods: periodResults,
    rows,
    totals: {
      conservative: add(...rows.map((r) => r.conservative)),
      base: add(...rows.map((r) => r.base)),
      upside: add(...rows.map((r) => r.upside)),
    },
  };
}

// The company-wide revenue-share budget until the admin sets one: the size of
// the equal pool the per-DJ offers replaced.
export const DEFAULT_BUDGET_PCT = 10;

export interface Committed {
  /** The shares of platform revenue promised in each partnership year, added up, in percent (Year 1 first). */
  byYear: number[];
  /** Offers added in. */
  counted: number;
  /** Offers left out because a share isn't known yet (a blank still to fill in). */
  skipped: string[];
}

// How much of Request's platform revenue is promised across ambassadors, year
// by year: every offer's share in each of its years, added up. Year 1 is each
// offer's own first year (offers have no start dates). An offer whose share
// isn't known yet is left out and named, never counted as 0%.
export function committedByYear(offers: OfferInputs[]): Committed {
  const byYear: number[] = [];
  const skipped: string[] = [];
  let counted = 0;
  for (const o of offers) {
    const rows = calculate(o).rows;
    if (rows.some((r) => r.ratePct === null)) {
      skipped.push(o.djName.trim() || "Unnamed offer");
      continue;
    }
    counted += 1;
    rows.forEach((r, k) => {
      byYear[k] = (byYear[k] ?? 0) + (r.ratePct as number);
    });
  }
  return { byYear, counted, skipped };
}

// What gets saved: tidy strings, the forecast cut to the duration (years past
// it are not part of this offer), and a gap-free schedule.
export function forSave(i: OfferInputs): OfferInputs {
  const years = clampYears(i.years);
  return {
    ...i,
    djName: i.djName.trim(),
    instagramHandle: i.instagramHandle.trim().replace(/^@+/, ""),
    notes: i.notes.trim(),
    score: { factors: i.score.factors.map((f) => ({ ...f, name: f.name.trim(), note: f.note.trim() })) },
    years,
    revenue: Array.from({ length: years }, (_, k) => val(i.revenue[k])),
    periods: normalizePeriods(i.periods, years),
  };
}

export const sameOffer = (a: OfferInputs, b: OfferInputs): boolean =>
  JSON.stringify(forSave(a)) === JSON.stringify(forSave(b));

// A saved offer as the server sent it. A missing or malformed field reads as
// blank, never as today's default: a saved offer shows what was saved.
type Loose = Record<string, unknown>;
const obj = (v: unknown): Loose => (v && typeof v === "object" && !Array.isArray(v) ? (v as Loose) : {});
const str = (v: unknown): string => (typeof v === "string" ? v : "");

export function inputsFrom(raw: unknown): OfferInputs {
  const r = obj(raw);
  const s = obj(r.scenarioPct);
  const years = clampYears(r.years);
  const periods = (Array.isArray(r.periods) ? r.periods : []).map((p): SharePeriod => {
    const o = obj(p);
    return { start: Number(o.start), end: Number(o.end), weight: val(o.weight), ratePct: val(o.ratePct) };
  });
  return {
    djName: str(r.djName),
    instagramHandle: str(r.instagramHandle),
    photo: str(r.photo),
    notes: str(r.notes),
    followers: val(r.followers),
    reelViews: val(r.reelViews),
    storyViews: val(r.storyViews),
    reelsPerQuarter: val(r.reelsPerQuarter),
    storiesPerQuarter: val(r.storiesPerQuarter),
    reelCpm: val(r.reelCpm),
    storyCpm: val(r.storyCpm),
    // Saved before the score existed: neutral ratings, and the multiplier picked
    // then stays as the one set by hand.
    score: { factors: scoreFactorsFrom(obj(r.score).factors) ?? neutralFactors(DEFAULT_FACTORS) },
    strategicMultiplier: val(r.strategicMultiplier),
    riskPremium: val(r.riskPremium),
    years,
    revenue: (Array.isArray(r.revenue) ? r.revenue : []).slice(0, MAX_YEARS).map(val),
    scenarioPct: { conservative: val(s.conservative), base: val(s.base), upside: val(s.upside) },
    periods: normalizePeriods(periods, years),
  };
}

export function savedFrom(raw: unknown): SavedOffer | null {
  const r = obj(raw);
  if (typeof r._id !== "string" || !r._id) return null;
  return {
    _id: r._id,
    inputs: inputsFrom(r.inputs),
    results: r.results && typeof r.results === "object" ? (r.results as OfferResults) : null,
    createdAt: typeof r.createdAt === "string" ? r.createdAt : null,
    updatedAt: typeof r.updatedAt === "string" ? r.updatedAt : null,
  };
}
